import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { getSessionApi, getGuestStore } from "../../../utils/demoApi";
import { AppButton } from "../../ui";
import { resolveSessionKind } from "../sessionKind";
import DemoPublicShell from "./DemoPublicShell";
import PostSessionSummary from "./PostSessionSummary";
import useAttendeeToken from "./useAttendeeToken";
import "../demo-classes.scss";

const CHOICES = [
  { value: "yes",   label: "Willing to join" },
  { value: "maybe", label: "Need more information" },
  { value: "no",    label: "Not interested" },
];

/**
 * The post-session feedback form, and the landing place for every chaser in the
 * feedback ladder (~15 min, +1 day, +3 days after the session).
 *
 * ⚠️ It asks `/me` before rendering anything. Two chasers can be open in one
 * inbox at once, so somebody who answered from the first and then clicks the
 * second must be thanked, not asked again — the form re-appearing reads as if
 * their answer was thrown away. The server treats a repeat submit as
 * `alreadySubmitted` rather than overwriting, so the two agree.
 *
 * ⚠️ The guest token is NOT cleared on submit any more. It used to be, on the
 * theory that a finished attendee should not rejoin a dead room — but the
 * credential is now what makes every later link in their inbox work, and
 * clearing it turned the next chaser they clicked into a registration form.
 * The room is protected by `status === 'live'` on the server, which is where
 * that guarantee belongs.
 */
const DemoThankYouPage = ({ kind = "demo" }) => {
  const { slug } = useParams();
  const vocab = resolveSessionKind(kind);
  const api = getSessionApi(vocab.kind);
  const token = useAttendeeToken(vocab.kind, slug);

  const [interested, setInterested] = useState("");
  const [comments, setComments] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // null while we do not yet know — rendering the form before the answer comes
  // back is what would flash "tell us how it went" at someone who already did.
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (!token) { setMe({ registered: false }); return undefined; }
    let cancelled = false;
    api.getMe(slug, token)
      .then(({ data }) => {
        if (cancelled) return;
        setMe(data);
        if (data.feedbackSubmitted) setSubmitted(true);
      })
      .catch(() => {
        // An expired credential is not worth an error here — they can still
        // give feedback, they just are not recognised.
        if (!cancelled) setMe({ registered: false });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, token, kind]);

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!interested) return setError("Please pick one option.");
    setSubmitting(true);
    setError("");
    try {
      await api.postSession(slug, token, {
        interested,
        extraNotes: comments.trim() || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      if (err?.response?.status === 401) {
        getGuestStore(vocab.kind).clear(slug);
        setError("Your link has expired. Please use the most recent email we sent you.");
      } else {
        setError(err?.response?.data?.message || "Could not submit feedback. Please try again.");
      }
    }
    setSubmitting(false);
  };

  if (me === null) {
    return (
      <DemoPublicShell kind={vocab.kind}>
        <p className="demo-public__pulse">One moment…</p>
      </DemoPublicShell>
    );
  }

  // The certificate, the recording and the group link belong on this page
  // whether or not feedback was given — a certificate email lands here too.
  const summary = me?.registered
    ? <PostSessionSummary me={me} api={api} slug={slug} token={token} />
    : null;

  if (submitted) {
    const alreadyHad = me?.feedbackSubmitted;
    return (
      <DemoPublicShell kind={vocab.kind}>
        {summary}
        <div className="demo-public__success-block">
          <CheckCircle2 size={48} className="demo-public__success-icon" />
          <h2 className="demo-public__title demo-public__title--spaced">
            {alreadyHad ? "You've already told us — thank you!" : "Thanks for your feedback!"}
          </h2>
          <p className="demo-public__subtitle">
            {alreadyHad
              ? "We have your response on file, so there's nothing more to do. If you sent this more than once, don't worry — we only counted it once."
              : "Our team will reach out to you with next steps. You can close this window now."}
          </p>
        </div>
      </DemoPublicShell>
    );
  }

  return (
    <DemoPublicShell kind={vocab.kind}>
      {summary}
      {me?.registered && !me.attended ? (
        <>
          <h2 className="demo-public__title">
            {me?.name ? `Sorry we missed you, ${me.name}` : "Sorry we missed you"}
          </h2>
          <p className="demo-public__subtitle">Would you like to hear about the next one? Let us know and our team will follow up.</p>
        </>
      ) : (
        <>
          <h2 className="demo-public__title">
            {me?.name ? `Thanks for joining, ${me.name}!` : "Thanks for joining!"}
          </h2>
          <p className="demo-public__subtitle">How did it go? Let us know and our team will follow up.</p>
        </>
      )}

      <form className="demo-public__form" onSubmit={handleSubmit} noValidate>
        <div className="demo-public__choices">
          {CHOICES.map((c) => (
            <AppButton
              key={c.value}
              type="button"
              variant={interested === c.value ? "default" : "outline"}
              onClick={() => setInterested(c.value)}
            >
              {c.label}
            </AppButton>
          ))}
        </div>

        <div className="demo-public__field">
          <label htmlFor="comments">Comments (optional)</label>
          <textarea
            id="comments"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Any questions, feedback, or things you'd like us to know…"
          />
        </div>

        {error && <div className="demo-public__error">{error}</div>}

        <AppButton variant="default" type="submit" disabled={submitting}>
          {submitting ? "Submitting…" : "Submit"}
        </AppButton>
      </form>
    </DemoPublicShell>
  );
};

export default DemoThankYouPage;
